import type { DrawContext } from './draw';
import type { View } from './project';
import { projectionMatrix } from './project';

/*
 * THE SCRIPTURE WASH — rendered once, then blitted for the rest of the session.
 *
 * Ported from `reference/universe-v8/engine.js` — `washLayer` (1324–1348). Checked against it by
 * comparing recorded draw calls into the offscreen surface.
 *
 * THIS IS THE FIX OUR OLD RENDERER NEVER HAD, and it is the one whose absence was measured. Ours
 * rebuilt the wash every frame from `Bundle.fields`: eighty clumps × six nested discs, each disc
 * scaling with the zoom, which came to **twenty-nine screens of overdraw per frame** and took the
 * opening flight to twenty frames a second. Theirs draws the whole layer into a 900×900 texture
 * ONCE and thereafter blits one image. The layer stops being a per-frame cost at all.
 *
 * It is memoised because it CAN be: the wash is a function of the roster, and the roster does not
 * change while somebody is reading. Zoom and pan move the blit, not the texture.
 */

/** Where a scripture's names gather: a centre, a reach, and how many are there. */
export interface WashClump {
  x: number;
  y: number;
  r: number;
  /** How many of that text's names gathered here — what sets the strength. */
  n: number;
}

/**
 * HOW WIDE A CLUMP IS, IN CELLS OF THE GRID IT WAS GATHERED ON — theirs, recovered from the data.
 *
 * Their `FIELDS` is eighty baked blobs, so the rule behind it had to be read back out of them. It
 * is `r = 1210 + 6.6n` capped at 2970, and the fit is exact: the worst residual over the 74 blobs
 * below the cap is four tenths of a unit. No two of their blobs share a cell of a 1,000-unit grid,
 * and against that cell those three numbers are the ones below.
 *
 * THE POINT IS THE UNIT. A radius means nothing on its own — what decides whether this layer reads
 * as weather or as a rash of spots is the radius against the DISTANCE TO THE NEXT CLUMP, and every
 * one of their blobs is wider than that distance: the ratio runs 1.51 to 3.22, median 2.08, so
 * each blob's skirt reaches well into its neighbours and the eighty of them resolve into four
 * clouds. Ours ran 0.44 to 0.60 — not one clump touched another — which is exactly what Ariel was
 * looking at when he said the star dust was too mixed to make a constellation: at that width the
 * eye reads the individual blobs, and a hundred separate blobs in five colours is a mess where one
 * cloud per scripture is a picture.
 *
 * It was checked against the wrong quantity before: the median radius as a share of the RIM, which
 * ours matched (12% against their 12-20%) while the picture stayed wrong. Two blobs can be the
 * right size for the world and still be too small for each other.
 *
 * The population term is deliberately weak — a clump of forty is 18% wider than a lone name, not
 * four times — because in their rule too the base is most of the width. Population is carried by
 * the ALPHA (`DENSITY_FLOOR`, `DENSITY_PER_NAME` below), which saturates at six names.
 */
export const THEIR_CLUMP_SIZE = { base: 1.21, perName: 0.0066, cap: 2.97 } as const;

/** A clump's radius, given the cell it was gathered in and how many names fell there. */
export function clumpRadius(cell: number, names: number): number {
  const size = THEIR_CLUMP_SIZE;
  return cell * Math.min(size.cap, size.base + size.perName * names);
}

/** A surface to draw the texture into, and the handle a caller will later blit. */
export interface WashSurface<C> {
  canvas: C;
  ctx: DrawContext;
}

/** How a caller makes one. Passed in rather than called here, so nothing in this file touches a DOM. */
export type WashCanvas<C> = (width: number, height: number) => WashSurface<C>;

/** The finished texture and the world rectangle it covers, ready to be blitted. */
export interface WashTexture<C> {
  canvas: C;
  x0: number;
  y0: number;
  w: number;
  h: number;
}

/**
 * What the wash is made of. All DATA — the clumps, the palette and the world's extent alike.
 *
 * `clumps` is per corpus key. Theirs comes from a `FIELDS` constant; OURS COMES FROM `clumpsFrom`
 * IN `our-scene.ts`, which bins the settled node positions in the browser.
 *
 * It used to say ours came from `Bundle.fields`, and that is the pipeline's `fields.ts` — which
 * this renderer never reads. Only `MapCanvas`, the renderer this port replaces, is fed from there.
 * A session spent an afternoon tuning `fields.ts` against screenshots of `?v8=1` on the strength of
 * this line: every clump count and every radius it changed was correct, shipped, and invisible.
 * What finally said so was a harness differencing the frame against one with the layer deleted —
 * the wash moved the picture by 0.25 of 255, against a noise floor of 0.2, however the pipeline's
 * fields were rewritten. See `e2e/mud-v8.mjs`.
 */
export interface WashPaint {
  corpusKeys: readonly string[];
  palette: Readonly<Record<string, string>>;
  clumps: Readonly<Record<string, readonly WashClump[]>>;
  /** Where the naming stops. The texture covers a little more than this. */
  rim: number;
  /** The world's vertical scale. */
  squash: number;
}

/**
 * THE TEXTURE'S RESOLUTION.
 *
 * Mechanism rather than data: it is a quality-for-memory choice, and because the world-to-texture
 * scale is derived from the rim, the same 900 gives the same detail per screen pixel however large
 * the world is.
 */
const TEXTURE = 900;

/** How far past the rim the texture reaches, so a wash at the edge is not clipped mid-falloff. */
const OVERSCAN = 1.15;

/** The smallest a clump may be drawn in the texture — below this it is a dot rather than a field. */
const MIN_TEXTURE_R = 2;

/*
 * HOW STRONG A CLUMP IS, and the one number in this file worth arguing about.
 *
 * Their formula, kept exactly: `0.16 × min(1, 0.4 + n × 0.1)`, which reaches full strength at six
 * names in a clump.
 *
 * IT IS KNOWN TO SATURATE ON OUR ROSTER. The first port measured this exact curve over 1,488 names
 * and found **74% of clumps at full strength and 63% at maximum reach** — an even fog instead of an
 * asymmetric bloom (`UNIVERSE-MAP.md` deviation 6). That was a fact about our clump populations,
 * not about the arithmetic, so the arithmetic is ported unchanged and the fix belongs where the
 * problem is: in how our `fields` are generated. Recorded in `V8-PORT.md` under section 5.
 */
const PEAK = 0.16;
const DENSITY_FLOOR = 0.4;
const DENSITY_PER_NAME = 0.1;
/** Where the falloff turns over, and how much is left there. */
const MID_STOP = 0.45;
const MID_SHARE = 0.38;

/** An alpha as the two hex digits their colours carry as a suffix. */
function hex(v: number): string {
  return Math.round(Math.max(0, Math.min(255, v * 255))).toString(16).padStart(2, '0');
}

/**
 * Draw the whole wash into one texture.
 *
 * Returns the surface's canvas and the world rectangle it maps to, which is everything a blit needs.
 */
export function buildWash<C>(make: WashCanvas<C>, paint: WashPaint): WashTexture<C> {
  const sq = paint.squash;
  const ext = paint.rim * OVERSCAN;
  const extY = ext * sq;
  const { canvas, ctx } = make(TEXTURE, TEXTURE);
  /* World to texture: the whole extent mapped onto the square, so the texture is square and the
     world rectangle it stands for is not. */
  const toTexture = (x: number, y: number): [number, number] => [
    ((x + ext) / (2 * ext)) * TEXTURE,
    ((y + extY) / (2 * extY)) * TEXTURE,
  ];
  const scale = TEXTURE / (2 * ext);

  for (const key of paint.corpusKeys) {
    const colour = paint.palette[key];
    if (colour === undefined) continue;
    for (const f of paint.clumps[key] ?? []) {
      const [lx, ly] = toTexture(f.x, f.y * sq);
      const lr = Math.max(MIN_TEXTURE_R, f.r * scale);
      const peak = PEAK * Math.min(1, DENSITY_FLOOR + f.n * DENSITY_PER_NAME);
      const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, lr);
      g.addColorStop(0, colour + hex(peak));
      g.addColorStop(MID_STOP, colour + hex(peak * MID_SHARE));
      g.addColorStop(1, `${colour}00`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(lx, ly, lr, 0, 6.2832);
      ctx.fill();
    }
  }

  return { canvas, x0: -ext, y0: -extY, w: 2 * ext, h: 2 * extY };
}

/*
 * ── AND HOW IT IS PUT ON THE SCREEN ──────────────────────────────────────────────────────────────
 *
 * Ported from `drawStatic` lines 1448–1462, which is where the texture above is finally used.
 *
 * One `drawImage` under the projection matrix. Their comment: *"the projection is affine, so the
 * cached field can ride it exactly, tilt and spin included"* — see `projectionMatrix` in
 * `project.ts` for why that is true, because it is the fact the whole cache rests on. Drawn afresh
 * this layer is eighty nested gradient discs; drawn this way it is one blit at any camera.
 *
 * IT FADES OUT AS THE READER COMES IN. The wash says where a scripture's names GATHER, which is an
 * answer to "what is the shape of this library" — a question nobody is asking once they are close
 * enough to read names. So it is gone by the time it would be clutter.
 */

/** How the fade is set up. A world dimension, and the one number here that is not the look. */
export interface WashFade {
  /**
   * The zoom at which the wash is fully gone, and how long it takes to get there.
   *
   * Theirs is 0.34 over a span of 0.3. These are ZOOMS, and a zoom is pixels per world unit — a
   * world ten times the size is read at a tenth the zoom, so a copied 0.34 would put the fade in
   * completely the wrong place. The first port learned this the expensive way: it shipped an
   * absolute `WASH_ZOOM_GONE = 0.66` and the wash stayed on through the whole opening flight,
   * which is where twenty-nine screens of overdraw a frame were being paid.
   */
  gone: number;
  span: number;
}

/** Below this the layer is not worth a blit. A fraction of alpha, so look rather than data. */
export const WASH_CUTOFF = 0.02;

/** How much of the wash is showing at this zoom: all of it far out, none of it close in. */
export function washFade(zoom: number, fade: WashFade): number {
  return Math.min(1, Math.max(0, (fade.gone - zoom) / fade.span));
}

/**
 * Blit the texture under the camera.
 *
 * The matrix is set, the image is drawn in WORLD coordinates — `x0`, `y0`, `w`, `h` are the world
 * rectangle `buildWash` reported — and the matrix is put back to device pixels afterwards, because
 * every layer after this one draws in screen space.
 */
export function drawWash<C>(
  ctx: DrawContext,
  view: View,
  texture: WashTexture<C>,
  fade: number,
  dpr: number,
): void {
  if (fade <= WASH_CUTOFF) return;
  const [a, b, c, d, e, f] = projectionMatrix(view, dpr);
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.setTransform(a, b, c, d, e, f);
  ctx.drawImage(texture.canvas, texture.x0, texture.y0, texture.w, texture.h);
  ctx.restore();
  /* `restore` puts the matrix back to whatever it was, and theirs does not trust that: it states
     the device-pixel transform again. Kept, because a layer drawn after a wash that had been
     skipped must land in the same place as one drawn after a wash that was not. */
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.globalAlpha = 1;
}

/**
 * The wash, built at most once.
 *
 * Their memo is a field on the component; this is the same thing with the "at most once" made
 * impossible to forget. Rebuilding it per frame is the twenty-nine-screen mistake, so the cache is
 * not an optimisation to be added later — it is the unit.
 */
export class WashCache<C> {
  private texture?: WashTexture<C>;

  /**
   * WHICH LAYER FILLS IT, because ours and theirs no longer draw the same one.
   *
   * `buildWash` is theirs, ported, and stays the default so every differential test against their
   * engine keeps comparing like with like. Ours passes `buildTerritory` instead — same texture, same
   * blit, a different question asked of the same clumps. See `territory.ts`.
   */
  constructor(private readonly build: (make: WashCanvas<C>, paint: WashPaint) => WashTexture<C> = buildWash) {}

  get(make: WashCanvas<C>, paint: WashPaint): WashTexture<C> {
    this.texture ??= this.build(make, paint);
    return this.texture;
  }

  /** Drop it, for when the roster itself changes — a rebuild, or a filter that changes the fields. */
  clear(): void {
    this.texture = undefined;
  }

  /** Whether it has been built. What a test asserts to prove "once" is really once. */
  get built(): boolean {
    return this.texture !== undefined;
  }
}
